import { StandardTextfield, useFetch, UseFetchOptions } from '@brandfolder/react';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, useContext, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';
import { object, SchemaOf, string } from 'yup';

import { defaultGettyTheme, defaultTheme } from '@constants/theme';
import { AsideContext } from '@context/aside';
import { BrandguideContext } from '@context/brandguide';
import { ThemeContext } from '@context/theme';
import { ApiDatum } from '@typings/api';
import { Theme, ThemeImage } from '@typings/theme';

interface SettingsHeaderCdnUrlValues {
  heroSrc?: string;
}

const extensions = ['.apng', '.avif', '.gif', '.jpeg', '.jpg', '.png', '.svg', '.webp'];

const schema: SchemaOf<SettingsHeaderCdnUrlValues> = object().shape({
  heroSrc: string()
    .url('Must be a valid URL')
    .test(
      'is-valid-format',
      'Must be one of .apng, .avif, .gif, .jpeg, .jpg, .png, .svg, or .webp',
      (value) => {
        if (!value) return true;
        return extensions.some((extension) => value.includes(extension));
      }
    )
});

interface SettingsHeaderCdnUrl extends HTMLAttributes<HTMLFormElement> {}

const UnstyledSettingsHeaderCdnUrl: FunctionComponent<SettingsHeaderCdnUrl> = (props) => {
  const { autoFocusId, setAutoFocusId } = useContext(AsideContext);
  const { brandguide } = useContext(BrandguideContext);
  const { theme, setTheme } = useContext(ThemeContext);

  const ref = useRef('');

  const heroSrcIsDefault =
    theme.image.heroSrc === defaultTheme.image.heroSrc ||
    theme.image.heroSrc === defaultGettyTheme.image.heroSrc;
  const { formState, handleSubmit, register } = useForm<SettingsHeaderCdnUrlValues>({
    defaultValues: {
      heroSrc: heroSrcIsDefault ? '' : theme.image.heroSrc
    },
    mode: 'onBlur',
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
    shouldFocusError: false
  });

  const { errors } = formState;

  const options: UseFetchOptions = {
    fetchOnMount: false,
    headers: {
      'Content-Type': 'application/json',
      'x-brandguide-key': brandguide?.key || ''
    },
    method: 'PATCH',
    url: `/api/themes/${theme.key}`
  };

  const { error, fetch, loading, response } = useFetch<ApiDatum<Partial<Theme>>>(options);

  useEffect(() => {
    if (autoFocusId === 'heroSrc') {
      setAutoFocusId('');
    }
  }, [autoFocusId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading) {
      ref.current = toast.loading('Saving...');
    }
  }, [loading]);

  useEffect(() => {
    if (response) {
      setTheme({
        ...theme,
        image: {
          ...theme.image,
          heroSrc: response.data.attributes.image?.heroSrc || defaultTheme.image.heroSrc
        }
      });
      toast.dismiss(ref.current);
      toast.success('Saved!');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    if (error) {
      toast.dismiss(ref.current);
      toast.error('Error saving.');
    }
  }, [error]);

  return (
    <form
      {...props}
      className={classNames({
        'global-settings__wrapper': true,
        [`${props.className}`]: !!props.className
      })}
      noValidate
      onBlur={handleSubmit((values) => {
        const image: ThemeImage = {
          faviconHref: theme.image.faviconHref,
          heroSrc: values.heroSrc || ''
        };

        fetch({
          ...options,
          body: {
            data: {
              attributes: {
                image
              }
            }
          }
        });
      })}
    >
      <StandardTextfield
        {...register('heroSrc')}
        className="heroSrc-textfield"
        error={errors.heroSrc?.message}
        label="Header CDN URL"
        helpText="Suggested size 2880x720"
        id="heroSrc"
        textfieldProps={{
          autoFocus: autoFocusId === 'heroSrc'
        }}
      />
    </form>
  );
};

const StyledSettingsHeaderCdnUrl = styled(UnstyledSettingsHeaderCdnUrl)`
  .heroSrc-textfield {
    margin-top: 24px;
  }

  .header-label-wrapper {
    align-items: center;
    display: flex;
  }
`;

export const SettingsHeaderCdnUrl: FunctionComponent<SettingsHeaderCdnUrl> = (props) => {
  return <StyledSettingsHeaderCdnUrl {...props} />;
};
